import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion';
import dotProp from 'dot-prop-immutable';
import {Link} from '@reach/router';
import axiosUI from '../../axiosUI';
import Sidebar from '../Sidebar/Sidebar';
import Topbar from '../Topbar/Topbar';
import Loader from '../Loader/Loader';
import Main from '../Main/Main';
import Input, { INPUT_TYPES } from '../Input/Input';
import Button from '../Button/Button';
import PopupWrapper from '../PopupWrapper';
import { withGlobalContext } from '../../App';
import {
  NOTIFICATIONS,
} from '../../constants';
import ResourceStatePill from '../ResourceStatePill';
import withCommonUtils from "../withCommonUtils";
import MarketSwitcher from "../MarketSwitcher";

class TranslationsPerMarket extends Component {
  static propTypes = {
    country: PropTypes.string,
    language: PropTypes.string,
    createNotification: PropTypes.func,
    navigate: PropTypes.func,
  };

  constructor(props) {
    super(props);

    this.state = {
      translationList: [],
      translationDrafts: [],
      showAddTranslationPopup: false,
      showBulkTranslationsPopup: false,
      newKeyName: '',
      bulkJson: '',
      showUnsavedChangesWarningPopup: false,
      loading: false,
    };
  }

  componentDidMount = async () => {
    this.reloadTranslations()
  };

  componentDidUpdate = async (prevProps) => {
    if (this.props.country !== prevProps.country || this.props.language !== prevProps.language) {
      await this.reloadTranslations();
    }
  };

  reloadTranslations = async () => {
    this.setState({ loading: true }, async () => {
      try {
        let { data } = await axiosUI.get(
          `/api/translations?salesChannel=${this.props.country}&locale=${this.props.language}`
        );
        const hasDrafts = data.some(translation => translation.state === 'DRAFT');
        let translationDrafts;
        if (hasDrafts) {
          translationDrafts = data.reduce((drafts, translation) => {
            if (translation.state === 'DRAFT') {
              drafts[translation.name] = true;
            }
            return drafts;
          }, {});

          // remove entries which are both in published and draft stage
          data = data.filter(translation => {
            const { state, name } = translation;
            return !(state === 'PUBLISHED' && translationDrafts[name]);
          });
        }
        this.setState({ translationList: data, translationDrafts, hasDrafts });
      } catch (err) {
        console.log(err);

        this.props.createNotification({
          message: `Failed to fetch translations`,
          type: NOTIFICATIONS.TYPE.ERROR,
          timeout: 3000,
        });
      }

      this.setState({ loading: false });
    })
  }

  getNewOperationType = translation => {
    const oldState = translation.state;
    const oldOperationType = translation.operation_type;
    if (
      oldState === 'PUBLISHED' ||
      (oldState === 'DRAFT' && oldOperationType === 'UPDATE')
    ) {
      return 'UPDATE';
    }
    return 'CREATE';
  };

  updateTranslation = event => {
    const { name, value } = event.target;
    const index = Number(name.split('.')[0]);
    const { translationList } = this.state;
    const currentTranslation = translationList[index];
    const updatedTranslation = {
      ...currentTranslation,
      content: value,
      operation_type: this.getNewOperationType(currentTranslation),
      state: 'UNSAVED',
    };
    const updatedTranslationList = dotProp.set(
      translationList,
      index,
      updatedTranslation
    );
    this.setState({ translationList: updatedTranslationList });
  };

  updateKeyName = event => {
    this.setState({ newKeyName: event.target.value });
  };

  toggleNewTranslationPopup = () => {
    this.setState(prevState => ({
      showAddTranslationPopup: !prevState.showAddTranslationPopup,
    }));
  };

  toggleBulkTranslationPopup = () => {
    console.log("toggled", this.state.showBulkTranslationsPopup);
    this.setState(prevState => ({
      showBulkTranslationsPopup: !prevState.showBulkTranslationsPopup,
    }));
  };

  updateBulkJson = event => {
    this.setState({ bulkJson: event.target.value });
  };

  importJson = (json) => {
    const data = JSON.parse(json);
    const translations = [];
    Object.keys(data).forEach(key =>{
      const newTranslation = {
        name: key,
        content: data[key],
        operation_type: 'CREATE',
        state: 'UNSAVED',
        sales_channel_id: this.props.country,
        locale: this.props.language,
      };
      translations.push(newTranslation);
    });
    this.setState(
        { translationList: [...translations, ...this.state.translationList] },
        () => {
          this.toggleBulkTranslationPopup();
        }
    );
  }


  toggleUnsavedChangesWarningPopup = () => {
    this.setState(prevState => ({
      showUnsavedChangesWarningPopup: !prevState.showUnsavedChangesWarningPopup,
    }));
  };

  createKey = () => {
    let { newKeyName } = this.state;
    newKeyName = newKeyName.toLowerCase().trim();

    // block and throw error if the key name has spaces
    if (newKeyName.includes(' ')) {
      return this.props.createNotification({
        message: `Translation key name cannot have spaces`,
        type: NOTIFICATIONS.TYPE.ERROR,
        timeout: 5000,
      });
    }

    const isDuplicate = this.state.translationList.some(
      translation => translation.name === newKeyName
    );

    // block and throw error if the key name is duplicate
    if (isDuplicate) {
      return this.props.createNotification({
        message: `Key - "${newKeyName}" already exists in the current list`,
        type: NOTIFICATIONS.TYPE.ERROR,
        timeout: 5000,
      });
    }

    const newTranslation = {
      name: this.state.newKeyName,
      content: '',
      operation_type: 'CREATE',
      state: 'UNSAVED',
      sales_channel_id: this.props.country,
      locale: this.props.language,
    };
    this.setState(
      { translationList: [newTranslation, ...this.state.translationList] },
      () => {
        this.setState({ newKeyName: '' });
        this.toggleNewTranslationPopup();
      }
    );
  };

  submitReview = async () => {
    // submit translations with UNSAVED state
    const newlyAddedAndUpdatedKeys = this.state.translationList.filter(
      translation => translation.state === 'UNSAVED'
    );

    try {
      const url = `/api/translations/`;
      await axiosUI.post(url, newlyAddedAndUpdatedKeys);

      this.props.createNotification({
        message: `Your changes are submitted for review`,
        type: NOTIFICATIONS.TYPE.SUCCESS,
        timeout: 5000,
      });

      setTimeout(() => {
        this.props.navigate('changeset');
      }, 3000);
    } catch (err) {
      console.log(err);
      this.props.createNotification({
        message: `Failed to save changes`,
        type: NOTIFICATIONS.TYPE.ERROR,
        timeout: 5000,
      });
    }

    return newlyAddedAndUpdatedKeys;
  };

  navigateToChangesetPage = () => {
    this.props.navigate('changeset');
  };

  render() {
    const hasUnsavedChanges = this.state.translationList.some(
      translation => translation.state === 'UNSAVED'
    );

    return (
      <Fragment>
        <Sidebar highlightNavItemIndex={1} />
        <Topbar className="market_translations__topbar flex justify-between items-center">
          <span className="font-bold text-lg capitalize">
            <Link
              className="bb-1-blue no-underline text-blue mr-2"
              to="/translations">
              Translations
            </Link>{' '}
            /
            <MarketSwitcher {...this.props} basePath='/translations'/>
          </span>
          <span className="float-right text-right">
            <PopupWrapper
              className="relative"
              isOpen={this.state.showAddTranslationPopup}
              togglePopup={this.toggleNewTranslationPopup}>
              <Button className="h-8" onClick={this.toggleNewTranslationPopup}>
                Add new Translation
              </Button>
              <AnimatePresence>
                {this.state.showAddTranslationPopup && (
                  <motion.div
                    initial={{ opacity: 0, y: '80%' }}
                    animate={{ opacity: 1, y: '100%' }}
                    exit={{ opacity: 0, y: '80%' }}
                    transition={{
                      ease: [0.18, 0.89, 0.39, 1.4],
                      duration: 0.3,
                    }}
                    className="button-popup absolute pin-r px-2 py-4 bg-white shadow-md rounded text-left w-64">
                    <Input
                      id="newKeyName"
                      name="newKeyName"
                      type={INPUT_TYPES.TEXT}
                      label="Name"
                      placeholder="Enter key name"
                      value={this.state.newKeyName}
                      onChange={this.updateKeyName}
                      inputClassName="shadow-inner bg-almostWhite"
                    />
                    <Button
                      type="green"
                      className="h-8 mt-4 w-full"
                      onClick={this.createKey}
                      disabled={!this.state.newKeyName.trim()}>
                      Create Key
                    </Button>
                  </motion.div>
                )}
              </AnimatePresence>
            </PopupWrapper>
            {this.state.hasDrafts && (
              <PopupWrapper
                className="relative"
                isOpen={this.state.showUnsavedChangesWarningPopup}
                togglePopup={this.toggleUnsavedChangesWarningPopup}>
                <Button
                  type="blue"
                  className="h-8 ml-8"
                  onClick={
                    hasUnsavedChanges
                      ? this.toggleUnsavedChangesWarningPopup
                      : this.navigateToChangesetPage
                  }>
                  Review Pending Draft
                </Button>
                <AnimatePresence>
                  {this.state.showUnsavedChangesWarningPopup && (
                    <motion.div
                      initial={{ opacity: 0, y: '80%' }}
                      animate={{ opacity: 1, y: '100%' }}
                      exit={{ opacity: 0, y: '80%' }}
                      transition={{
                        ease: [0.18, 0.89, 0.39, 1.4],
                        duration: 0.3,
                      }}
                      className="button-popup absolute pin-r px-2 py-4 bg-white shadow-md rounded text-left w-64">
                      <div className="text-center leading-normal">
                        There are some unsaved changes. Are you sure you want to
                        leave this page?
                      </div>
                      <div className="flex justify-around mt-8">
                        <Button
                          className="h-8"
                          onClick={this.toggleUnsavedChangesWarningPopup}>
                          No
                        </Button>
                        <Button
                          type="red-transparent"
                          className="h-8"
                          onClick={this.navigateToChangesetPage}>
                          Yes, leave this page
                        </Button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </PopupWrapper>
            )}
            <PopupWrapper
                className="relative"
                isOpen={this.state.showBulkTranslationsPopup}
                togglePopup={this.toggleBulkTranslationPopup}>
              <Button className="h-8" onClick={this.toggleBulkTranslationPopup}>
                Bulk Import
              </Button>
              <AnimatePresence>
                {this.state.showBulkTranslationsPopup && (
                    <motion.div
                        initial={{ opacity: 0, y: '80%' }}
                        animate={{ opacity: 1, y: '100%' }}
                        exit={{ opacity: 0, y: '80%' }}
                        transition={{
                          ease: [0.18, 0.89, 0.39, 1.4],
                          duration: 0.3,
                        }}
                        className="button-popup absolute pin-r px-2 py-4 bg-white shadow-md rounded text-left w-64">
                      <Input
                          id="bulkJson"
                          name="bulkJson"
                          type={INPUT_TYPES.TEXTAREA}
                          label="Json"
                          placeholder='{"key1": "value1", "key2": "value2"}'
                          value={this.state.bulkJson}
                          onChange={this.updateBulkJson}
                          inputClassName="shadow-inner bg-almostWhite"
                      />
                      <Button
                          type="green"
                          className="h-8 mt-4 w-full"
                          onClick={() => this.importJson(this.state.bulkJson)}>
                        Import
                      </Button>
                    </motion.div>
                )}
              </AnimatePresence>
            </PopupWrapper>
            <Button
              type="green"
              className="h-8 ml-8"
              disabled={!hasUnsavedChanges}
              onClick={this.submitReview}>
              Save as Draft
            </Button>
          </span>
        </Topbar>
        <Main className="market_translations__main overflow-x-hidden">
          <Loader loading={this.state.loading}>
            <ul className="list-reset">
              <AnimatePresence initial={false}>
                {this.state.translationList.map((translation, index) => {
                  const { name, content, state } = translation;
                  return (
                    <motion.li
                      initial={{ opacity: 0, y: '-30%', x: '0%' }}
                      animate={{ opacity: 1, y: '0%', x: '0%' }}
                      exit={{ opacity: 0, y: '0%', x: '30%' }}
                      transition={{
                        ease: [0.18, 0.89, 0.39, 1.4],
                        duration: 0.5,
                      }}
                      className="flex relative items-center py-4"
                      key={name}>
                      <div className="market_translations__key text-sm">
                        {name}
                      </div>
                      <Input
                        className="flex-grow mx-4"
                        id={`${index}.content`}
                        name={`${index}.content`}
                        type={INPUT_TYPES.TEXT}
                        placeholder={`Enter "${name}" translation`}
                        value={content}
                        onChange={this.updateTranslation}
                      />
                      <div className="w-24">
                        <ResourceStatePill state={state} />
                      </div>
                    </motion.li>
                  );
                })}
              </AnimatePresence>
            </ul>
          </Loader>
        </Main>
      </Fragment>
    );
  }
}

export default  withCommonUtils(withGlobalContext(TranslationsPerMarket));
